import { DollarSign, Search } from "lucide-react";
import { formatDate } from "../../utils/formatters";

const DepositsTab = ({
  allDeposits,
  regularUsers,
  depositForm,
  setDepositForm,
  showDepositUserDropdown,
  setShowDepositUserDropdown,
  selectedUserForDeposit,
  setSelectedUserForDeposit,
  depositUserSearch,
  setDepositUserSearch,
  onCreateDeposit
}) => {
  return (
    <div>
      <h2 className="font-chivo font-bold text-xl text-white mb-6">Пополнение баланса</h2>
      
      {/* Create Deposit Form */}
      <div className="bg-[#0A0A0A] rounded-lg border border-[#1F1F1F] p-6 mb-6">
        <h3 className="font-bold text-white mb-4">Новое пополнение</h3>
        <form onSubmit={onCreateDeposit} className="space-y-4">
          {/* User Search/Select */}
          <div className="relative">
            <label className="block text-[#A1A1AA] text-sm mb-2">Клиент *</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
              <input
                type="text"
                value={selectedUserForDeposit ? `${selectedUserForDeposit.name} (${selectedUserForDeposit.email})` : depositUserSearch}
                onChange={(e) => {
                  setDepositUserSearch(e.target.value);
                  setSelectedUserForDeposit(null);
                  setShowDepositUserDropdown(true);
                }}
                onFocus={() => setShowDepositUserDropdown(true)}
                placeholder="Поиск по ID, имени или email..."
                className="w-full bg-[#000000] border border-[#1F1F1F] rounded pl-10 pr-4 py-3 text-white placeholder-[#A1A1AA] focus:border-white focus:outline-none"
              />
            </div>
            {showDepositUserDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-[#0A0A0A] border border-[#1F1F1F] rounded z-10 max-h-60 overflow-y-auto">
                {regularUsers
                  .filter(u => 
                    depositUserSearch === "" || 
                    u.email.toLowerCase().includes(depositUserSearch.toLowerCase()) ||
                    u.name?.toLowerCase().includes(depositUserSearch.toLowerCase()) ||
                    u.client_id?.toLowerCase().includes(depositUserSearch.toLowerCase()) ||
                    u.id?.toLowerCase().includes(depositUserSearch.toLowerCase())
                  )
                  .slice(0, 10)
                  .map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => {
                        setDepositForm({ ...depositForm, user_id: u.id });
                        setSelectedUserForDeposit(u);
                        setShowDepositUserDropdown(false);
                        setDepositUserSearch("");
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-[#000000] transition-colors"
                    >
                      <div className="font-medium text-white">{u.name}</div>
                      <div className="text-[#A1A1AA] text-sm">{u.email} • Баланс: ${u.balance?.toFixed(2)}</div>
                    </button>
                  ))}
                {regularUsers.filter(u => 
                  depositUserSearch === "" || 
                  u.email.toLowerCase().includes(depositUserSearch.toLowerCase()) ||
                  u.name?.toLowerCase().includes(depositUserSearch.toLowerCase())
                ).length === 0 && (
                  <div className="px-4 py-3 text-[#A1A1AA]">Пользователи не найдены</div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Amount */}
            <div>
              <label className="block text-[#A1A1AA] text-sm mb-2">Сумма *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={depositForm.amount}
                onChange={(e) => setDepositForm({ ...depositForm, amount: e.target.value })}
                placeholder="0.00"
                className="w-full bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white placeholder-[#A1A1AA] focus:border-white focus:outline-none"
                required
              />
            </div>

            {/* Asset */}
            <div>
              <label className="block text-[#A1A1AA] text-sm mb-2">Актив</label>
              <select
                value={depositForm.asset}
                onChange={(e) => setDepositForm({ ...depositForm, asset: e.target.value })}
                className="w-full bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white focus:border-white focus:outline-none"
              >
                <option value="USDT">USDT</option>
              </select>
            </div>

            {/* Network */}
            <div>
              <label className="block text-[#A1A1AA] text-sm mb-2">Сеть</label>
              <select
                value={depositForm.network}
                onChange={(e) => setDepositForm({ ...depositForm, network: e.target.value })}
                className="w-full bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white focus:border-white focus:outline-none"
              >
                <option value="TRC-20">TRC-20</option>
              </select>
            </div>
          </div>

          <button
            type="submit"
            className="bg-[#34D399] hover:bg-[#00A843] text-white font-bold py-3 px-6 rounded flex items-center gap-2 transition-colors"
          >
            <DollarSign size={18} />
            Зачислить депозит
          </button>
        </form>
      </div>

      {/* Deposits History */}
      <div className="bg-[#0A0A0A] rounded-lg border border-[#1F1F1F] overflow-hidden">
        <div className="p-4 border-b border-[#1F1F1F]">
          <h3 className="font-bold text-white">История пополнений</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#1F1F1F]">
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-3 px-4">Дата</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-3 px-4">Клиент</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-3 px-4">Сумма</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-3 px-4">Актив</th>
                <th className="text-left text-[#A1A1AA] text-sm font-normal py-3 px-4">Сеть</th>
              </tr>
            </thead>
            <tbody>
              {allDeposits.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-[#A1A1AA]">
                    Нет пополнений
                  </td>
                </tr>
              ) : (
                allDeposits.map((d) => (
                  <tr key={d.id} className="border-b border-[#1F1F1F]/50 hover:bg-[#1A1A1A]/50">
                    <td className="py-3 px-4 text-[#A1A1AA] text-sm font-mono">
                      {formatDate(d.created_at)}
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-white text-sm">{d.user_name}</div>
                      <div className="text-[#A1A1AA] text-xs">{d.user_email}</div>
                    </td>
                    <td className="py-3 px-4 font-mono text-[#34D399]">
                      +${d.amount.toFixed(2)}
                    </td>
                    <td className="py-3 px-4 text-white">
                      {d.asset}
                    </td>
                    <td className="py-3 px-4 text-white">
                      {d.network}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DepositsTab;
